import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FlatList, Platform, Pressable, TextInput, View, type TextInputInstance } from 'react-native';
import { useInputStyle } from './Form';
import { Icon, type IconName } from './Icon';
import { Modal } from './Modal';
import { Text } from './Text';
import { radius, useTheme } from './theme';

export interface PaletteItem {
  key: string;
  title: string;
  subtitle?: string;
  group: string;
  icon: IconName;
  keywords?: string;
  onSelect: () => void;
}

/**
 * Searchable launcher. Static `items` are filtered locally; async results (e.g. employees) are
 * passed in `extraItems`, driven by `onQueryChange`. Arrow keys + Enter work on web.
 */
export function CommandPalette({
  open,
  onClose,
  items,
  extraItems,
  onQueryChange,
  placeholder = 'Jump to a screen or search employees…',
}: {
  open: boolean;
  onClose: () => void;
  items: PaletteItem[];
  extraItems?: PaletteItem[];
  onQueryChange?: (q: string) => void;
  placeholder?: string;
}) {
  const { colors } = useTheme();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const input = useRef<TextInputInstance>(null);
  const base = useInputStyle();
  useEffect(() => {
    if (open) {
      setQ('');
      setActive(0);
      setTimeout(() => input.current?.focus(), 50);
    }
  }, [open]);
  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    const local = s ? items.filter((i) => `${i.title} ${i.group} ${i.keywords ?? ''}`.toLowerCase().includes(s)) : items;
    return [...local.slice(0, 40), ...(s ? extraItems ?? [] : [])];
  }, [q, items, extraItems]);
  useEffect(() => {
    setActive(0);
    onQueryChange?.(q);
  }, [q, onQueryChange]);
  const pick = (i: PaletteItem | undefined) => {
    if (!i) return;
    onClose();
    i.onSelect();
  };
  const onKeyPress = (e: { nativeEvent: { key: string }; preventDefault?: () => void }) => {
    if (Platform.OS !== 'web') return;
    const k = e.nativeEvent.key;
    if (k === 'ArrowDown') {
      e.preventDefault?.();
      setActive((a) => Math.min(results.length - 1, a + 1));
    } else if (k === 'ArrowUp') {
      e.preventDefault?.();
      setActive((a) => Math.max(0, a - 1));
    } else if (k === 'Escape') onClose();
  };
  return (
    <Modal open={open} onClose={onClose} width={600} noScroll testID="command-palette">
      <View style={{ padding: 12, borderBottomWidth: 1, borderBottomColor: colors.border, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Icon name="search" size={18} color={colors.muted} />
        <TextInput
          ref={input}
          autoFocus
          value={q}
          onChangeText={setQ}
          placeholder={placeholder}
          placeholderTextColor={colors.faint}
          accessibilityLabel="Command palette search"
          onKeyPress={onKeyPress}
          onSubmitEditing={() => pick(results[active])}
          autoCorrect={false}
          autoCapitalize="none"
          style={[base, { flex: 1, borderWidth: 0, backgroundColor: 'transparent', paddingHorizontal: 4 }]}
        />
      </View>
      <FlatList
        data={results}
        keyExtractor={(i) => i.key}
        style={{ maxHeight: 440, flexGrow: 0 }}
        contentContainerStyle={{ padding: 8 }}
        keyboardShouldPersistTaps="handled"
        ListEmptyComponent={<Text variant="muted" style={{ padding: 16 }}>No results</Text>}
        renderItem={({ item, index }) => (
          <Pressable
            onPress={() => pick(item)}
            accessibilityRole="button"
            style={({ pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 10, borderRadius: radius.md, backgroundColor: index === active ? colors.primarySoft : pressed ? colors.hover : 'transparent' })}
          >
            <Icon name={item.icon} size={16} color={index === active ? colors.primary : colors.muted} />
            <View style={{ flex: 1 }}>
              <Text weight="medium">{item.title}</Text>
              {!!item.subtitle && <Text variant="small">{item.subtitle}</Text>}
            </View>
            <Text variant="caption">{item.group}</Text>
          </Pressable>
        )}
      />
      {Platform.OS === 'web' && (
        <View style={{ padding: 8, borderTopWidth: 1, borderTopColor: colors.border }}>
          <Text variant="small" align="center">
            ↑ ↓ to navigate · Enter to open · Esc to close
          </Text>
        </View>
      )}
    </Modal>
  );
}
